import { makeStyles } from '@material-ui/core';

export const useStyles = makeStyles(theme => ({
  contextGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  contextLabel: {
    color: theme.palette.text.secondary,
  },
  contextValue: {
    fontWeight: theme.typography.fontWeightBold as number,
  },
  scoringNote: {
    fontSize: 12,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  // The standalone page lays its content out as white cards on the gray
  // canvas; the modal carries its own surface, so these are page-only.
  card: {
    border: theme.customBorders.gray200,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    // The card's own padding meets the first question; the shared question
    // padding only separates questions from each other. A comments-only
    // template opens with the recommendation instead.
    '& $question:first-child, & $recommendation:first-child': {
      paddingTop: 0,
    },
  },
  questions: {
    display: 'flex',
    flexDirection: 'column',
  },
  question: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: theme.spacing(3, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  questionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  questionHeading: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  inputControl: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 5,
    },
    // MUI dims placeholders below AA contrast; hold them at secondary text.
    '& .MuiOutlinedInput-input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  // The single-line notes input at the design's 40px height.
  notesInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 5,
      height: 40,
      padding: 0,
    },
    '& .MuiOutlinedInput-input': {
      padding: '0 16px',
      height: '100%',
    },
    // MUI dims placeholders below AA contrast; hold them at secondary text.
    '& .MuiOutlinedInput-input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  questionTitle: {
    fontWeight: theme.typography.fontWeightBold as number,
    color: theme.palette.text.primary,
  },
  questionDescription: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  requiredMark: {
    color: theme.palette.error.main,
  },
  ratingGroup: {
    flexShrink: 0,
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 14,
  },
  ratingOption: {
    marginLeft: 0,
    marginRight: 0,
    // The IconButton padding around the radio would inflate the 14px gap.
    '& .MuiRadio-root': {
      padding: 0,
      marginBottom: theme.spacing(0.5),
    },
    '& .MuiSvgIcon-root': {
      width: 16,
      height: 16,
    },
    '& .MuiFormControlLabel-label': {
      fontSize: 11,
    },
  },
  choiceGroup: {
    flexShrink: 0,
    display: 'flex',
    gap: theme.spacing(1),
    '& .MuiToggleButtonGroup-grouped': {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 5,
      marginLeft: 0,
    },
    '& .MuiToggleButtonGroup-grouped:not(:first-child)': {
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderRadius: 5,
      marginLeft: 0,
    },
    '& .MuiToggleButtonGroup-grouped:not(:last-child)': {
      borderRadius: 5,
    },
  },
  recommendation: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(3, 0, 2),
  },
  recommendationGroup: {
    display: 'flex',
    width: '100%',
    gap: theme.spacing(2),
    '& .MuiToggleButtonGroup-grouped': {
      borderRadius: 5,
      marginLeft: 0,
    },
    '& .MuiToggleButtonGroup-grouped:not(:first-child)': {
      borderLeft: `1px solid ${theme.palette.divider}`,
      marginLeft: 0,
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  recommendationOption: {
    flex: 1,
    textTransform: 'none',
    padding: '9px 22px',
    fontSize: 14,
    fontWeight: theme.typography.fontWeightBold as number,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 5,
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  choiceOption: {
    textTransform: 'none',
    padding: '9px 22px',
    fontSize: 14,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  // Replaces the FDS ModalContent padding so the panel divider runs flush
  // from the header border to the footer border; each pane carries its own
  // padding instead.
  modalContent: {
    padding: 0,
    display: 'flex',
    overflow: 'hidden',
  },
  modalContentDividers: {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  // Each pane scrolls on its own, so the applicant panel stays in view
  // while the evaluator scrolls the questions.
  modalBody: {
    flex: 1,
    minWidth: 0,
    maxHeight: '50vh',
    display: 'flex',
    alignItems: 'stretch',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  applicantPanel: {
    width: 232,
    flexShrink: 0,
    overflowY: 'auto',
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    padding: theme.spacing(2.5, 2.5, 3, 3),
    borderRight: `1px solid ${theme.palette.divider}`,
    wordBreak: 'break-word',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  questionsPane: {
    flex: 1,
    minWidth: 0,
    overflowY: 'auto',
    padding: theme.spacing(2.5, 3, 1),
  },
  sidebarName: {
    fontWeight: theme.typography.fontWeightBold as number,
    color: theme.palette.text.primary,
  },
  liveScore: {
    color: theme.palette.text.primary,
  },
  liveScoreValue: {
    fontSize: 16,
    fontWeight: theme.typography.fontWeightMedium as number,
  },
  footer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  footerStatus: {
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  footerInfo: {
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  footerSpacer: {
    marginRight: 'auto',
  },
  validationError: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    // red600, not error.main (red400): without the banner background the
    // foreground carries the contrast alone, and red400 on white fails AA.
    color: theme.palette.common.red600,
  },
  validationErrorIcon: {
    fontSize: theme.typography.fontSize,
  },
  savedIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    color: theme.palette.success.dark,
  },
  savedIcon: {
    fontSize: theme.typography.fontSize,
  },
  submittedCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(6, 4),
  },
  submittedBadge: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: theme.palette.common.green100,
    color: theme.palette.common.green800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  submittedBody: {
    color: theme.palette.text.secondary,
    maxWidth: 420,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6),
  },
}));
